import { ArrowLink } from "../../../../common/components/Link";
import { type SyntheticEvent } from "react";
import { type PdpPageProductData } from "../../../models/PdpPageProductData";
import { trackPdpClick } from "../../../../feature/Analytics/ga/ga";
import { URL_CONTACT_US } from "../../../../utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import { PREFERRED_PRODUCTS_ID } from "~/ProductDetails/constants";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import type { ProductData } from "~/models/ProductData";
import { userIsPublic, userIsRegistered } from "~/utils/roles";
import { ProductContext } from "~/feature/Product/ProductContext";
import { useEwellixContactForm } from "~/common/components/Button/PreconfiguredButtons/EwellixRequestAQuoteButton";
import { ExternalBrand } from "~/dto/ProductDataDTO";

export const getBaseType = (product: ProductDescriptor<PdpPageProductData>) => product.base.preferredProductsFamilyName;

export const PreferredProductsLink = ({ baseType, className }: { baseType: string; className?: string }) => {
  const { t } = useTranslation();

  return (
    <ArrowLink className={className} href={"#" + PREFERRED_PRODUCTS_ID}>
      {t("pdp.preferredProducts.title", { code: baseType })}
    </ArrowLink>
  );
};

export const ContactSalesTeamLink = ({ product, className }: { product: ProductData; className?: string }) => {
  const { t } = useTranslation();

  if (product.externalBrand === ExternalBrand.Ewellix) {
    return <EwellixContactUsLink product={product} className={className} />;
  }

  const track = () => trackPdpClick("Contact Sales Team", product.designation);

  let props;
  if (product.externalProduct) {
    props = {
      href: URL_CONTACT_US,
      onClick: () => {
        track();
      },
      children: t("addProduct.contactUs"),
    };
  } else {
    const isPublic = userIsPublic() || userIsRegistered();

    const translationKey = isPublic ? "addProduct.contactUs" : "addProduct.contactSales";
    props = {
      href: URL_CONTACT_US,
      onClick: (e: SyntheticEvent) => {
        track();
      },
      children: t(translationKey),
    };
  }

  return <ArrowLink className={className} {...props} />;
};

const EwellixContactUsLink = ({ product, className }: { product: ProductData; className?: string }) => {
  const { onClick, loading, dialog } = useEwellixContactForm(product);
  const { t } = useTranslation();

  return (
    <ProductContext value={product}>
      <ArrowLink className={className} disabled={loading} onClick={onClick}>
        {t("addProduct.contactUs")}
      </ArrowLink>
      {dialog}
    </ProductContext>
  );
};
